import React from 'react'

import {
  ArrowLeft, ArrowRight, ArrowToLeft, ArrowToRight, classnames, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, number, shape, string } from 'prop-types'

import buildPageHref from 'Pods/Shared/Utils/buildPageHref'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const VISIBLE_PAGES = 5

const visiblePages = (currentPage, totalPages) => {
  const half = Math.floor(VISIBLE_PAGES / 2)
  const start = Math.max(1, Math.min(currentPage - half, totalPages - VISIBLE_PAGES + 1))
  const end = Math.min(totalPages, start + VISIBLE_PAGES - 1)

  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
}

// Modified clicks (new tab, etc.) keep the native anchor navigation to the real page URL.
const isModifiedClick = (event) => (
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0
)

function CrawlablePagination({
  baseUrl,
  currentPage,
  totalPages,
  onPageChange,
  labels = EMPTY_OBJECT,
}) {
  if (totalPages < 2) {
    return null
  }

  const page = Number(currentPage)
  const isFirst = page <= 1
  const isLast = page >= totalPages

  const handleClick = (event, targetPage) => {
    if (isModifiedClick(event)) {
      return
    }
    event.preventDefault()
    onPageChange(targetPage)
  }

  const renderLink = ({
    targetPage, label, children, isCurrent = false, isDisabled = false,
  }) => {
    const className = classnames(
      'listing-pagination-links__link',
      'cgt-body-base',
      isCurrent && 'listing-pagination-links__link--current',
      isDisabled && 'listing-pagination-links__link--disabled'
    )

    return (
      <li className="listing-pagination-links__item" key={label}>
        {isDisabled ? (
          <span className={className} aria-disabled="true" aria-label={label}>{children}</span>
        ) : (
          <a
            href={buildPageHref(baseUrl, targetPage)}
            className={className}
            aria-label={label}
            aria-current={isCurrent ? 'page' : undefined}
            onClick={(event) => handleClick(event, targetPage)}
          >
            {children}
          </a>
        )}
      </li>
    )
  }

  // Rendered inside the shared Pagination's navigation landmark, so no landmark of its own.
  return (
    <div className="listing-pagination-links listing-pagination-links--inline">
      <ul className="listing-pagination-links__list">
        {renderLink({
          targetPage: 1,
          label: labels.firstPage,
          children: <Icon svgComponent={ArrowToLeft} />,
          isDisabled: isFirst,
        })}
        {renderLink({
          targetPage: page - 1,
          label: labels.previousPage,
          children: <Icon svgComponent={ArrowLeft} />,
          isDisabled: isFirst,
        })}
        {visiblePages(page, totalPages).map((pageNumber) => renderLink({
          targetPage: pageNumber,
          label: `${pageNumber}`,
          children: pageNumber,
          isCurrent: pageNumber === page,
        }))}
        {renderLink({
          targetPage: page + 1,
          label: labels.nextPage,
          children: <Icon svgComponent={ArrowRight} />,
          isDisabled: isLast,
        })}
        {renderLink({
          targetPage: totalPages,
          label: labels.lastPage,
          children: <Icon svgComponent={ArrowToRight} />,
          isDisabled: isLast,
        })}
      </ul>
    </div>
  )
}

CrawlablePagination.propTypes = {
  baseUrl: string.isRequired,
  currentPage: number.isRequired,
  totalPages: number.isRequired,
  onPageChange: func.isRequired,
  labels: shape({
    firstPage: string,
    previousPage: string,
    nextPage: string,
    lastPage: string,
  }),
}

export default CrawlablePagination
